import { useState, useEffect, useCallback, RefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import { MapAction, MapState } from '../LocationMapContent';

type InitializeMapProps = {
  mapContainerRef: RefObject<HTMLDivElement>;
  state: MapState;
  dispatch: React.Dispatch<MapAction>;
};

/**
 * Der externe Kartenstil verweist auf Bilder, die es nicht gibt, und Mapbox
 * meldet jedes davon als `Image "<name>" could not be loaded` in der Konsole.
 * Zwei Quellen:
 *
 * 1. Der Layer "water-pattern" trägt `"fill-pattern": ""` — ein leerer Name.
 * 2. Die POI-Layer bilden ihren Namen mit `"icon-image": "{class}_11"` aus den
 *    Vektordaten. OpenMapTiles liefert mehr class-Werte, als das Sprite-Sheet
 *    Icons hat (101), also fehlt je nach Bildausschnitt ein anderer Teil.
 *
 * Beides ist serverseitig nicht abschliessend zu beheben. Ein 1x1 Pixel
 * grosses, transparentes Platzhalterbild erfüllt die Referenz, ohne das
 * Kartenbild zu verändern: die betroffenen Layer zeichnen heute nichts und
 * danach eine transparente Fläche.
 */
const TRANSPARENT_PIXEL = {
  width: 1,
  height: 1,
  data: new Uint8Array(4),
};

const DEFAULT_ZOOM = 10;
const MAP_CENTER: [number, number] = [7.9, 47.46];
const STYLE_URL = 'https://maps.fhnw.ch/res/style-cdn.json';

const useInitializeMap = ({
  mapContainerRef,
  state,
  dispatch,
}: InitializeMapProps) => {
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | undefined>(
    undefined,
  );

  const handleFullscreenChange = useCallback(() => {
    if (!mapInstance) return;
    dispatch({
      type: 'SET_FULLSCREEN',
      payload: document.fullscreenElement === mapInstance.getContainer(),
    });
  }, [dispatch, mapInstance]);

  const initialize = useCallback(() => {
    if (!mapContainerRef.current || mapInstance) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: STYLE_URL,
      center: MAP_CENTER,
      zoom: state.activeLocation?.zoomLevel || DEFAULT_ZOOM,
      interactive: true,
      scrollZoom: false,
    });

    // Ohne Guard auf den Namen: `map.hasImage('')` scheidet als Prüfung aus,
    // weil Mapbox einen leeren Namen dort mit einem ErrorEvent quittiert. Nötig
    // ist sie ohnehin nicht — das Event feuert nur, solange das Bild fehlt.
    map.on('styleimagemissing', ({ id }: { id: string }) =>
      map.addImage(id, TRANSPARENT_PIXEL),
    );

    map.on('load', () => {
      map.addSource('universities', {
        type: 'geojson',
        data: '/universities.json',
      });
      map.addLayer({
        id: 'university-buildings',
        type: 'fill',
        source: 'universities',
        paint: { 'fill-color': '#FDE70D', 'fill-opacity': 0.5 },
        filter: ['==', '$type', 'Polygon'],
      });
      map.addLayer({
        id: 'housenumber-labels',
        type: 'symbol',
        source: 'universities',
        minzoom: 15,
        layout: {
          'text-field': '{Housenumber}',
          'text-justify': 'center',
          'text-font': ['Noto Sans Bold'],
        },
        paint: {
          'text-color': '#000',
          'text-halo-color': '#fff',
          'text-halo-width': 2,
        },
      });
    });

    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    map.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');

    dispatch({
      type: 'SET_MAP_AND_IS_CLIENT',
      payload: { map, isClient: true },
    });

    setMapInstance(map);

    document.addEventListener('fullscreenchange', handleFullscreenChange);
  }, [
    mapContainerRef,
    handleFullscreenChange,
    setMapInstance,
    dispatch,
    mapInstance,
    state.activeLocation?.zoomLevel,
  ]);

  const uninitialize = useCallback(() => {
    mapInstance?.remove();
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [handleFullscreenChange, mapInstance]);

  useEffect(() => {
    initialize();
    return uninitialize;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialize, uninitialize]);

  return { initialize };
};

export default useInitializeMap;
